'use client';

import { useFileInput } from '@instamenu/react-hooks';
import { useDropzone } from 'react-dropzone';
import { useCallback } from 'react';
import { themeVars } from '@instamenu/ui-vanilla-extract';
import { Text } from '../text/text';
import { Flex } from '../flex/flex';
import { useLocale } from '@instamenu/context-i18n';

export const DropzoneArea = ({
  setFile,
  children,
  label,
  style,
  className,
  variant = 'default',
}: {
  setFile: (base64: string, filename?: string) => void;
  children: React.ReactNode;
  label?: string;
  style?: React.CSSProperties;
  className?: string;
  variant?: 'default' | 'compact';
}) => {
  const { i18n } = useLocale();
  const handleFileSelect = useFileInput((base64, filename) => setFile(base64, filename));

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      acceptedFiles.forEach((file) => handleFileSelect(file));
    },
    [handleFileSelect],
  );

  const isCompact = variant === 'compact';

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: isCompact
      ? { 'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'] }
      : {
          'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
          'application/pdf': ['.pdf'],
        },
    multiple: !isCompact,
    noClick: !isCompact,
  });

  return (
    <div
      style={{
        position: 'relative',
        cursor: isCompact ? 'pointer' : undefined,
        ...style,
      }}
      className={className}
      {...getRootProps()}
    >
      <input {...getInputProps()} />
      {isDragActive && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: themeVars.color.brand,
            opacity: 0.9,
            color: 'white',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: isCompact ? '4px' : '8px',
            zIndex: 10,
            fontSize: isCompact ? '12px' : '18px',
            fontWeight: 600,
            border: isCompact
              ? '2px dashed rgba(255, 255, 255, 0.8)'
              : '3px dashed rgba(255, 255, 255, 0.8)',
          }}
        >
          {isCompact ? (
            <Text style={{ color: 'white', fontSize: 12 }}>
              {label ?? i18n.t('dropzone.label')}
            </Text>
          ) : (
            <Flex
              css={{
                gap: themeVars.pad[12],
                alignItems: 'center',
                flexWrap: 'wrap',
                padding: themeVars.pad[24],
                justifyContent: 'center',
              }}
            >
              <img src="/avocado-dropzone.png" alt="Dropzone" width={80} height={80} />
              <Text>{label ?? i18n.t('dropzone.label')}</Text>
            </Flex>
          )}
        </div>
      )}
      {children}
    </div>
  );
};
